import React, { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import LoginFun from "./Loginfun";

// ── Session countdown ────────────────────────────────────────────────────────
//
// The sign-in lasts as long as the identity token the portal holds
// (ExpirretokenTime, in unix seconds). This shows how long is left and, in the
// last two minutes, asks whether to stay signed in. "Stay signed in" asks the
// identity service for a fresh token; if that is refused, or nobody answers, the
// patient is signed out when the time runs out — as they always were.

const WARN_AT = 120; // seconds

const SCOPES = [
  "openid",
  "profile",
  "https://oxar.onmicrosoft.com/c7e3a74b-6465-4ef2-9700-3ab424414914/Write",
  "https://oxar.onmicrosoft.com/c7e3a74b-6465-4ef2-9700-3ab424414914/Read",
];

export function formatRemaining(s) {
  if (s === null || s === undefined) return "";
  const t = Math.max(0, s);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = t % 60;
  const mm = String(m).padStart(h ? 2 : 1, "0");
  return `${h ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

// Seconds left, ticking every second; null when there is no sign-in to count.
export function useSessionRemaining() {
  const read = () => {
    const exp = Number(localStorage.getItem("ExpirretokenTime"));
    return exp ? exp - Math.floor(Date.now() / 1000) : null;
  };
  const [remaining, setRemaining] = useState(read);
  useEffect(() => {
    const id = setInterval(() => setRemaining(read()), 1000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

// The small clock for the user menu.
export function SessionClock() {
  const remaining = useSessionRemaining();
  if (remaining === null) return null;
  return (
    <span className={`ox-sessionclock${remaining <= WARN_AT ? " is-low" : ""}`} title="Time until you are signed out">
      <i className="fa-regular fa-clock" aria-hidden="true" /> Signed out in {formatRemaining(remaining)}
    </span>
  );
}

export default function SessionWatcher() {
  const { instance } = useMsal();
  const auth = LoginFun();
  const remaining = useSessionRemaining();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const signOut = useCallback(() => { auth.logout(); }, [auth]);

  // Time is up: sign out, as the portal always did.
  useEffect(() => {
    if (remaining !== null && remaining <= 0) signOut();
  }, [remaining]); // eslint-disable-line react-hooks/exhaustive-deps

  const stay = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const account = instance.getAllAccounts()[0];
      const resp = await instance.acquireTokenSilent({ scopes: SCOPES, account, forceRefresh: true });
      const exp = resp?.idTokenClaims?.exp || resp?.account?.idTokenClaims?.exp;
      if (!exp || exp - Math.floor(Date.now() / 1000) <= WARN_AT) throw new Error("not extended");
      sessionStorage.setItem("AuthToken", resp.accessToken);
      localStorage.setItem("ExpirretokenTime", exp);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (remaining === null || remaining > WARN_AT || remaining <= 0) return null;

  return (
    <div className="ox-modal" role="alertdialog" aria-modal="true" aria-labelledby="ox-session-h" aria-describedby="ox-session-d">
      <div className="ox-modal__box ox-session">
        <header><h3 id="ox-session-h">Are you still there?</h3></header>
        <div className="ox-session__body" id="ox-session-d">
          <p className="ox-session__count" aria-live="polite">{formatRemaining(remaining)}</p>
          <p>For your privacy you will be signed out when the time runs out. Do you want to stay signed in?</p>
          {failed && <p className="ox-session__err" role="alert">We couldn't extend your session. Please sign out and sign in again.</p>}
        </div>
        <footer>
          <button type="button" className="ox-btn" onClick={signOut}>Sign out now</button>
          <button type="button" className="ox-btn ox-btn--primary" onClick={stay} disabled={busy} autoFocus>
            {busy ? "Extending..." : "Stay signed in"}
          </button>
        </footer>
      </div>
    </div>
  );
}
